// soccer_admin/src/pages/PremiumNameRequests.jsx
import React, { useEffect, useState } from 'react';
import {
  StarIcon, UserIcon, CalendarIcon, CheckCircleIcon,
  XCircleIcon, ClockIcon, PlusIcon, TrashIcon,
  ArrowPathIcon, MagnifyingGlassIcon, EyeIcon, PhotoIcon
} from '@heroicons/react/24/outline';
import {
  fetchPremiumPurchases,
  approvePremiumPurchase,
  rejectPremiumPurchase,
  addPremiumName,
  deletePremiumName,
  fetchPremiumNameStats
} from '../services/api';

const statusColors = {
  pending: 'bg-yellow-600/20 text-yellow-400',
  approved: 'bg-green-600/20 text-green-400',
  rejected: 'bg-red-600/20 text-red-400'
};

const statusLabels = {
  pending: 'Pending',
  approved: 'Approved',
  rejected: 'Rejected'
};

const tierBadges = {
  gold: 'bg-yellow-600 text-white',
  silver: 'bg-gray-400 text-gray-900',
  bronze: 'bg-amber-700 text-white'
};

const tierOptions = ['gold', 'silver', 'bronze'];

const PremiumNameRequests = () => {
  const [purchases, setPurchases] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newName, setNewName] = useState({ name: '', tier: 'gold', price: 100 });
  const [searchTerm, setSearchTerm] = useState('');
  
  // ===== State for viewing screenshot =====
  const [viewingImage, setViewingImage] = useState(null);
  const [imageLoading, setImageLoading] = useState(false);
  const [imageError, setImageError] = useState(false);

  const loadData = async () => {
    setLoading(true);
    try {
      const [purchasesRes, statsRes] = await Promise.all([
        fetchPremiumPurchases(),
        fetchPremiumNameStats()
      ]);
      setPurchases(purchasesRes.data);
      setStats(statsRes.data);
    } catch (err) {
      console.error(err);
      alert('Failed to load premium name data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleApprove = async (purchaseId) => {
    if (!window.confirm('Approve this purchase? The user will receive the team name.')) return;
    setUpdatingId(purchaseId);
    try {
      await approvePremiumPurchase(purchaseId);
      await loadData();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to approve');
    } finally {
      setUpdatingId(null);
    }
  };

  const handleReject = async (purchaseId) => {
    const reason = prompt('Enter rejection reason (optional):');
    if (reason === null) return; // cancelled
    setUpdatingId(purchaseId);
    try {
      await rejectPremiumPurchase(purchaseId, reason || undefined);
      await loadData();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to reject');
    } finally {
      setUpdatingId(null);
    }
  };

  const handleAddName = async (e) => {
    e.preventDefault();
    if (!newName.name.trim() || !newName.price) return;
    try {
      await addPremiumName(newName.name.trim(), newName.tier, parseInt(newName.price));
      setShowAddModal(false);
      setNewName({ name: '', tier: 'gold', price: 100 });
      await loadData();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to add name');
    }
  };

  const handleDeleteName = async (id, name) => {
    if (!window.confirm(`Delete "${name}"? This cannot be undone.`)) return;
    try {
      await deletePremiumName(id);
      await loadData();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete');
    }
  };

  const filteredPurchases = purchases.filter(p =>
    p.premium_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.username?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // ===== Open image viewer =====
  const openImageViewer = (imagePath) => {
    setViewingImage(imagePath);
    setImageLoading(true);
    setImageError(false);
  };

  // ===== Close image viewer =====
  const closeImageViewer = () => {
    setViewingImage(null);
    setImageLoading(false);
    setImageError(false);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-yellow-500"></div>
        <span className="ml-3 text-white text-lg">Loading...</span>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-white flex items-center gap-3">
              <StarIcon className="h-8 w-8 text-yellow-400" />
              Premium Name Requests
            </h1>
            <p className="text-gray-400 mt-2">Manage purchases of premium team names</p>
          </div>
          <div className="flex gap-3 flex-wrap">
            <button
              onClick={() => setShowAddModal(true)}
              className="bg-yellow-500 hover:bg-yellow-600 text-black font-bold px-4 py-2 rounded-lg flex items-center gap-2 transition"
            >
              <PlusIcon className="h-5 w-5" /> Add Name
            </button>
            <span className="bg-yellow-500/20 text-yellow-300 px-3 py-1 rounded-full text-sm font-medium">
              Pending: {stats?.pending_requests || 0}
            </span>
            <span className="bg-gray-600/30 text-gray-300 px-3 py-1 rounded-full text-sm font-medium">
              Revenue: ${stats?.total_revenue || 0}
            </span>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
          <div className="text-gray-400 text-sm">Total Names</div>
          <div className="text-2xl font-bold text-white">{stats?.total_names || 0}</div>
        </div>
        <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
          <div className="text-gray-400 text-sm">Sold</div>
          <div className="text-2xl font-bold text-green-400">{stats?.sold_names || 0}</div>
        </div>
        <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
          <div className="text-gray-400 text-sm">Available</div>
          <div className="text-2xl font-bold text-yellow-400">{stats?.available_names || 0}</div>
        </div>
        <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
          <div className="text-gray-400 text-sm">Pending Requests</div>
          <div className="text-2xl font-bold text-orange-400">{stats?.pending_requests || 0}</div>
        </div>
      </div>

      {/* Filter */}
      <div className="mb-4 relative">
        <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
        <input
          type="text"
          placeholder="Search by name or user..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-500"
        />
      </div>

      {/* Desktop Table */}
      <div className="hidden md:block bg-gray-800 rounded-xl border border-gray-700 overflow-hidden shadow-xl">
        <div className="overflow-x-auto">
          <table className="min-w-full text-white">
            <thead className="bg-gray-700/50 border-b border-gray-700">
              <tr className="text-left text-gray-400 text-sm">
                <th className="px-4 py-3">ID</th>
                <th className="px-4 py-3">User</th>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Tier</th>
                <th className="px-4 py-3">Price</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Screenshot</th>
                <th className="px-4 py-3">TxID</th>
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3 text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredPurchases.map((p) => (
                <tr key={p.purchase_id} className="border-b border-gray-700/50 hover:bg-gray-700/30 transition">
                  <td className="px-4 py-3 text-gray-400">#{p.purchase_id}</td>
                  <td className="px-4 py-3">
                    <div className="flex flex-col">
                      <span className="font-medium text-white">{p.username}</span>
                      <span className="text-xs text-gray-400">{p.display_name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 font-medium text-yellow-300">{p.premium_name}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${tierBadges[p.tier] || 'bg-gray-600'}`}>
                      {p.tier}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-white">${p.price_paid}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${statusColors[p.status]}`}>
                      {statusLabels[p.status]}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {p.screenshot_path ? (
                      <button
                        onClick={() => openImageViewer(p.screenshot_path)}
                        className="text-blue-400 hover:text-blue-300 transition"
                        title="View Screenshot"
                      >
                        <PhotoIcon className="h-5 w-5" />
                      </button>
                    ) : (
                      <span className="text-gray-500 text-xs">No image</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs font-mono text-gray-400 max-w-[100px] truncate" title={p.txid}>
                    {p.txid}
                  </td>
                  <td className="px-4 py-3 text-gray-400 text-sm">
                    {new Date(p.purchased_at).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-center gap-2">
                      {p.status === 'pending' && (
                        <>
                          <button
                            onClick={() => handleApprove(p.purchase_id)}
                            disabled={updatingId === p.purchase_id}
                            className="text-green-400 hover:text-green-300 transition disabled:opacity-50"
                            title="Approve"
                          >
                            <CheckCircleIcon className="h-5 w-5" />
                          </button>
                          <button
                            onClick={() => handleReject(p.purchase_id)}
                            disabled={updatingId === p.purchase_id}
                            className="text-red-400 hover:text-red-300 transition disabled:opacity-50"
                            title="Reject"
                          >
                            <XCircleIcon className="h-5 w-5" />
                          </button>
                        </>
                      )}
                      {updatingId === p.purchase_id && (
                        <ArrowPathIcon className="h-5 w-5 animate-spin text-yellow-400" />
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {filteredPurchases.length === 0 && (
          <div className="text-center text-gray-400 py-12">No purchases found.</div>
        )}
      </div>

      {/* Mobile Cards */}
      <div className="md:hidden space-y-4">
        {filteredPurchases.map((p) => (
          <div key={p.purchase_id} className="bg-gray-800 rounded-xl p-4 border border-gray-700 shadow-lg">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-white font-bold">{p.username}</p>
                <p className="text-gray-400 text-sm">{p.display_name}</p>
              </div>
              <span className={`px-2 py-1 rounded-full text-xs font-semibold ${statusColors[p.status]}`}>
                {statusLabels[p.status]}
              </span>
            </div>
            <div className="mt-2 grid grid-cols-2 gap-2 text-sm">
              <div><span className="text-gray-400">Name:</span> <span className="text-yellow-300 font-medium">{p.premium_name}</span></div>
              <div><span className="text-gray-400">Tier:</span> <span className={`px-2 py-0.5 rounded-full text-xs ${tierBadges[p.tier]}`}>{p.tier}</span></div>
              <div><span className="text-gray-400">Price:</span> <span className="text-white">${p.price_paid}</span></div>
              <div><span className="text-gray-400">Date:</span> <span className="text-white">{new Date(p.purchased_at).toLocaleDateString()}</span></div>
            </div>
            <div className="mt-2 text-xs text-gray-400 truncate flex items-center justify-between">
              <span>TxID: {p.txid}</span>
              {p.screenshot_path && (
                <button
                  onClick={() => openImageViewer(p.screenshot_path)}
                  className="text-blue-400 hover:text-blue-300 transition flex items-center gap-1 text-xs"
                >
                  <PhotoIcon className="h-4 w-4" /> View
                </button>
              )}
            </div>
            <div className="mt-3 flex gap-2">
              {p.status === 'pending' && (
                <>
                  <button
                    onClick={() => handleApprove(p.purchase_id)}
                    disabled={updatingId === p.purchase_id}
                    className="flex-1 bg-green-600 hover:bg-green-700 text-white py-1.5 rounded-lg text-sm font-semibold transition flex items-center justify-center gap-1"
                  >
                    <CheckCircleIcon className="h-4 w-4" /> Approve
                  </button>
                  <button
                    onClick={() => handleReject(p.purchase_id)}
                    disabled={updatingId === p.purchase_id}
                    className="flex-1 bg-red-600 hover:bg-red-700 text-white py-1.5 rounded-lg text-sm font-semibold transition flex items-center justify-center gap-1"
                  >
                    <XCircleIcon className="h-4 w-4" /> Reject
                  </button>
                </>
              )}
            </div>
          </div>
        ))}
        {filteredPurchases.length === 0 && (
          <div className="text-center text-gray-400 py-12">No purchases found.</div>
        )}
      </div>

      {/* Screenshot Modal with loading and error states */}
      {viewingImage && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
          onClick={closeImageViewer}
        >
          <div
            className="max-w-2xl max-h-[90vh] bg-gray-900 rounded-2xl p-4 border border-yellow-500/30 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-white font-bold">Payment Screenshot</h3>
              <button
                onClick={closeImageViewer}
                className="text-gray-400 hover:text-white text-xl transition"
              >
                ✕
              </button>
            </div>

            {/* Loading state */}
            {imageLoading && (
              <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-yellow-500"></div>
              </div>
            )}

            {/* Image */}
            <img
              src={`https://api.soccerup.games${viewingImage}`}
              alt="Payment Screenshot"
              className={`max-w-full max-h-[70vh] rounded-lg object-contain transition-opacity duration-300 ${
                imageLoading ? 'opacity-0' : 'opacity-100'
              }`}
              onLoad={() => setImageLoading(false)}
              onError={(e) => {
                setImageLoading(false);
                setImageError(true);
                e.target.onerror = null;
                e.target.src = '/img/no-image.png';
              }}
            />

            {/* Error state */}
            {imageError && (
              <div className="text-center text-red-400 py-8">
                <p className="text-lg">Failed to load image</p>
                <p className="text-sm text-gray-400 mt-2">The screenshot file may be missing or corrupted.</p>
                <button
                  onClick={() => window.open(`https://api.soccerup.games${viewingImage}`, '_blank')}
                  className="mt-4 inline-block bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm transition"
                >
                  Open Image in New Tab
                </button>
              </div>
            )}

            <p className="text-gray-400 text-xs mt-2 text-center">Click outside to close</p>
          </div>
        </div>
      )}

      {/* Add Name Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
          <div className="bg-gray-800 rounded-2xl max-w-md w-full p-6 border border-yellow-500/30 shadow-2xl">
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
              <PlusIcon className="h-6 w-6 text-yellow-400" /> Add Premium Name
            </h2>
            <form onSubmit={handleAddName} className="space-y-4">
              <div>
                <label className="block text-gray-400 text-sm mb-1">Name</label>
                <input
                  type="text"
                  value={newName.name}
                  onChange={(e) => setNewName({ ...newName, name: e.target.value })}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-yellow-500"
                  placeholder="e.g., Royal Madrid"
                  required
                />
              </div>
              <div>
                <label className="block text-gray-400 text-sm mb-1">Tier</label>
                <select
                  value={newName.tier}
                  onChange={(e) => setNewName({ ...newName, tier: e.target.value })}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-yellow-500"
                >
                  {tierOptions.map(t => (
                    <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-gray-400 text-sm mb-1">Price (USD)</label>
                <input
                  type="number"
                  value={newName.price}
                  onChange={(e) => setNewName({ ...newName, price: parseInt(e.target.value) || 0 })}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-yellow-500"
                  min="1"
                  required
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  type="submit"
                  className="flex-1 bg-yellow-500 hover:bg-yellow-600 text-black font-bold py-2 rounded-lg transition"
                >
                  Add Name
                </button>
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="flex-1 bg-gray-600 hover:bg-gray-500 text-white py-2 rounded-lg transition"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PremiumNameRequests;